import { useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { Info, Pencil } from "lucide-react";
import { selectAuthUser } from "store/reducer";
import ForgotPasswordResetPanel from "pages/settings/ForgotPasswordResetPanel";
import { Button } from "components/ui/button";
import { Label } from "components/ui/label";
import { PasswordInput } from "components/ui/password-input";
import api, { isSuccessResponse } from "lib/api";
import { API_ENDPOINTS } from "lib/apiUrl";
import swtAlert from "lib/sweetAlert";
import {
  validatePassword,
  validateConfirmPassword,
  validateForm as runFormValidation,
  required,
} from "lib/validation";

const EMPTY_FORM = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

export default function SettingsChangePasswordPage() {
  const user = useSelector(selectAuthUser);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const resetForm = useCallback(() => {
    setFormData(EMPTY_FORM);
    setErrors({});
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: "" } : prev));
  }, []);

  const validateForm = useCallback(() => {
    const schema = {
      currentPassword: required("Current password"),
      newPassword: (v) => validatePassword(v),
      confirmPassword: (v) => validateConfirmPassword(v, formData.newPassword),
    };
    const newErrors = runFormValidation(formData, schema);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleCancelEdit = useCallback(() => {
    resetForm();
    setIsEditing(false);
  }, [resetForm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditing) return;

    if (!validateForm()) {
      await swtAlert.warning(
        "Check your password",
        "Please fill in all password fields correctly."
      );
      return;
    }

    setSaving(true);
    try {
      const resp = await api.post(API_ENDPOINTS.changePassword, {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      });
      if (!isSuccessResponse(resp)) {
        await swtAlert.error(
          "Could not change password",
          resp?.message || "Please verify your current password and try again."
        );
        return;
      }
      resetForm();
      setIsEditing(false);
      await swtAlert.success(
        "Password updated",
        resp?.message || "Your password has been changed."
      );
    } catch (err) {
      await swtAlert.error(
        "Could not change password",
        err?.message || "Please try again or use forgot password."
      );
    } finally {
      setSaving(false);
    }
  };

  const fieldStack = "space-y-2";
  const fieldDisabled = !isEditing;

  return (
    <>
      <Helmet>
        <title>Change password | Fortify</title>
      </Helmet>
      <div>
        <div className="max-w-xl">
          <div className="flex items-center gap-1">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              Change password
            </h1>
            {isEditing ? null : (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0 text-muted-foreground hover:text-foreground"
                onClick={() => setIsEditing(true)}
                aria-label="Edit password"
              >
                <Pencil className="h-5 w-5" />
              </Button>
            )}
          </div>
          <div className="mt-2 h-0.5 w-full bg-accent" aria-hidden />
        </div>

        <div className="mt-6 flex max-w-xl items-start gap-2 rounded-md border border-border bg-card/80 px-3 py-2.5 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden />
          <p>
            {isEditing
              ? "Enter your current password, then choose a new strong password you do not use elsewhere."
              : "Your password is hidden for security. Click the edit icon to change it."}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 max-w-xl space-y-6">
          <div className={fieldStack}>
            <Label htmlFor="currentPassword">Current password</Label>
            <PasswordInput
              id="currentPassword"
              name="currentPassword"
              placeholder="Current password"
              value={formData.currentPassword}
              onChange={handleChange}
              disabled={fieldDisabled}
              autoComplete="current-password"
              aria-invalid={!!errors.currentPassword}
            />
            {errors.currentPassword ? (
              <p className="text-xs text-destructive">{errors.currentPassword}</p>
            ) : null}
          </div>

          <div className={fieldStack}>
            <Label htmlFor="newPassword">New password</Label>
            <PasswordInput
              id="newPassword"
              name="newPassword"
              placeholder="New password"
              value={formData.newPassword}
              onChange={handleChange}
              disabled={fieldDisabled}
              autoComplete="new-password"
              aria-invalid={!!errors.newPassword}
            />
            {errors.newPassword ? (
              <p className="text-xs text-destructive">{errors.newPassword}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Min. 8 characters with uppercase, lowercase, number &amp; special character.
              </p>
            )}
          </div>

          <div className={fieldStack}>
            <Label htmlFor="confirmPassword">Confirm new password</Label>
            <PasswordInput
              id="confirmPassword"
              name="confirmPassword"
              placeholder="Confirm new password"
              value={formData.confirmPassword}
              onChange={handleChange}
              disabled={fieldDisabled}
              autoComplete="new-password"
              aria-invalid={!!errors.confirmPassword}
            />
            {errors.confirmPassword ? (
              <p className="text-xs text-destructive">{errors.confirmPassword}</p>
            ) : null}
          </div>

          {isEditing ? (
            <div className="flex flex-wrap gap-3 pt-2">
              <Button type="submit" size="lg" disabled={saving}>
                {saving ? "Updating…" : "Update password"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="lg"
                disabled={saving}
                onClick={handleCancelEdit}
              >
                Cancel
              </Button>
            </div>
          ) : null}
        </form>

        <p className="mt-6 max-w-xl text-sm text-muted-foreground">
          <button
            type="button"
            className="font-medium text-accent hover:underline"
            onClick={() => setShowForgotPassword(true)}
          >
            Forgot your password?
          </button>
        </p>

        {showForgotPassword ? (
          <div className="mt-4 max-w-xl">
            <ForgotPasswordResetPanel
              defaultEmail={user?.email ?? ""}
              onHide={() => setShowForgotPassword(false)}
            />
          </div>
        ) : null}
      </div>
    </>
  );
}
